import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiSliders, FiClock, FiZap, FiHeart } from 'react-icons/fi';
import { recipes } from '../data/mockData';

const RecipeCard = ({ recipe }) => (
  <motion.div 
    whileHover={{ y: -4 }}
    className="panel flex flex-col group cursor-pointer"
  >
    <div className="relative h-48 overflow-hidden bg-steel-100">
      <img 
        src={recipe.image} 
        alt={recipe.title} 
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-steel-400 hover:text-tomato-500 shadow-sm transition-colors">
        <SafeIcon icon={FiHeart} />
      </div>
      <div className="absolute bottom-3 left-3 flex gap-2">
        {recipe.tags.slice(0,2).map(tag => (
          <span key={tag} className="px-2 py-1 bg-steel-900/80 backdrop-blur text-white text-[10px] font-mono rounded uppercase tracking-wide">
            {tag}
          </span>
        ))}
      </div>
    </div>
    
    <div className="p-4 flex-1 flex flex-col">
      <h3 className="font-bold text-steel-900 text-lg mb-1 leading-tight">{recipe.title}</h3>
      
      <div className="flex items-center gap-4 text-sm text-steel-500 mb-4 font-mono text-xs">
        <span className="flex items-center gap-1"><SafeIcon icon={FiClock} /> {recipe.time}</span>
        <span className="flex items-center gap-1"><SafeIcon icon={FiZap} /> {recipe.cals} cal</span>
      </div>

      <div className="mt-auto grid grid-cols-3 gap-2 border-t border-steel-100 pt-3">
        <div className="text-center">
          <div className="text-[10px] text-steel-400 font-mono">PRO</div>
          <div className="font-medium text-sm text-steel-700">{recipe.macros.p}</div>
        </div>
        <div className="text-center border-x border-steel-100">
          <div className="text-[10px] text-steel-400 font-mono">CARB</div>
          <div className="font-medium text-sm text-steel-700">{recipe.macros.c}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] text-steel-400 font-mono">FAT</div>
          <div className="font-medium text-sm text-steel-700">{recipe.macros.f}</div>
        </div>
      </div>
    </div>
  </motion.div>
);

export const Recipes = () => {
  const [filter, setFilter] = useState('All');
  const filters = ['All', 'High Protein', 'Prep', 'Quick'];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-steel-900 tracking-tight">Recipe Database</h1>
          <p className="text-steel-500">142 verified modules available.</p>
        </div>
        
        <div className="flex w-full md:w-auto gap-2">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400" />
            <input 
              type="text" 
              placeholder="Search parameters..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-steel-200 rounded-md text-sm focus:outline-none focus:border-tomato-500 focus:ring-1 focus:ring-tomato-500 transition-shadow"
            />
          </div>
          <button className="p-2 bg-white border border-steel-200 rounded-md text-steel-600 hover:bg-steel-50 transition-colors">
            <SafeIcon icon={FiSliders} />
          </button>
        </div>
      </header>

      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {filters.map(f => (
          <button 
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f 
                ? 'bg-steel-900 text-white' 
                : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {recipes.map(recipe => (
          <RecipeCard key={recipe.id} recipe={recipe} />
        ))}
      </div>
    </motion.div>
  );
};